import { Link } from "@tanstack/react-router";
import { Linkedin, Facebook, Mail } from "lucide-react";
import { pathFor } from "@/i18n/slugMap";
import { shared } from "@/i18n/shared";
import { useLocale } from "@/i18n/LocaleProvider";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { Img, SIZES } from "@/components/Img";

const EMPLY_URL =
  "https://misupport.career.emply.com/da/?utm_source=misupport_web&utm_medium=web";

export function SiteFooter() {
  const locale = useLocale();
  const t = shared[locale];

  return (
    <footer className="bg-background border-t border-white/10 text-foreground">
      <div className="mx-auto max-w-7xl px-4 md:px-8 py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Link to={pathFor("home", locale)} aria-label="MI Support">
            <Img
              src="/wp-content/uploads/2024/09/Logomark-White.png"
              alt="MI Support"
              className="h-12 w-auto mb-4"
              sizes={SIZES.logo}
            />
          </Link>
          <p className="font-semibold">MI Support A/S</p>
          <p className="text-foreground/80 text-sm mt-2">Korskildelund 4, 1.<br />2670 Greve</p>
          <p className="text-foreground/80 text-sm mt-2">CVR: 25793714</p>
          <div className="mt-4 space-y-1 text-sm">
            <a href="mailto:info@misupport.dk" className="block text-foreground/80 hover:text-primary">
              info@misupport.dk
            </a>
            <a href="tel:+4555551212" className="block text-foreground/80 hover:text-primary">
              +45 5555 1212
            </a>
          </div>
          <div className="mt-4 flex gap-3">
            <a href="https://www.linkedin.com/company/misupport/" aria-label="LinkedIn" className="p-2 rounded-full bg-white/10 hover:bg-primary transition-colors">
              <Linkedin className="h-4 w-4" />
            </a>
            <a href="https://www.facebook.com/misupport" aria-label="Facebook" className="p-2 rounded-full bg-white/10 hover:bg-primary transition-colors">
              <Facebook className="h-4 w-4" />
            </a>
            <a href="mailto:info@misupport.dk" aria-label="Email" className="p-2 rounded-full bg-white/10 hover:bg-primary transition-colors">
              <Mail className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div>
          <p className="font-semibold mb-4">{t.footer.servicesHeading}</p>
          <ul className="space-y-2 text-sm text-foreground/80">
            <li><Link to={pathFor("ydelser", locale)} className="hover:text-primary">{t.footer.svcByggeledelse}</Link></li>
            <li><Link to={pathFor("ydelser", locale)} className="hover:text-primary">{t.footer.svcProjektledelse}</Link></li>
            <li><Link to={pathFor("ydelser", locale)} className="hover:text-primary">{t.footer.svcHse}</Link></li>
            <li><Link to={pathFor("sitecompliance", locale)} className="hover:text-primary">{t.footer.svcSiteCompliance}</Link></li>
            <li><Link to={pathFor("gratissikkerhedstjek", locale)} className="hover:text-primary">{t.footer.freeSafetyCheck}</Link></li>
            <li><Link to={pathFor("ydelser", locale)} className="hover:text-primary">{t.footer.svcIkt}</Link></li>
            <li><Link to={pathFor("ydelser", locale)} className="hover:text-primary">{t.footer.svcPlanning}</Link></li>
            <li><Link to={pathFor("ydelser", locale)} className="hover:text-primary">{t.footer.svcControlling}</Link></li>
          </ul>
        </div>

        <div>
          <p className="font-semibold mb-4">{t.footer.aboutHeading}</p>
          <ul className="space-y-2 text-sm text-foreground/80">
            <li><Link to={pathFor("omos", locale)} className="hover:text-primary">{t.nav.aboutMi}</Link></li>
            <li><Link to={pathFor("samarbejdspartnere", locale)} className="hover:text-primary">{t.nav.partners}</Link></li>
            <li><Link to={pathFor("viden", locale)} className="hover:text-primary">{t.nav.viden}</Link></li>
            <li><a href={EMPLY_URL} target="_blank" rel="noopener noreferrer" className="hover:text-primary">{t.footer.career}</a></li>
            <li><a href="https://www.misupportit.dk/" className="hover:text-primary">{t.nav.misupportIt}</a></li>
            <li><Link to={pathFor("cookiepolitik", locale)} className="hover:text-primary">{t.footer.legalCookie}</Link></li>
            <li><Link to={pathFor("persondata", locale)} className="hover:text-primary">{t.footer.legalPersondata}</Link></li>
          </ul>
        </div>

        <div>
          <p className="font-semibold mb-4">{t.footer.contactHeading}</p>
          <p className="text-sm text-foreground/80 mb-4">{t.footer.contactBlurb}</p>
          <Link
            to={pathFor("kontakt", locale)}
            className="inline-block rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            {t.cta.contactUs}
          </Link>
          <LanguageSwitcher className="mt-6" />
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-foreground/60">
        © {new Date().getFullYear()} MI Support A/S. {t.footer.rights}
      </div>
    </footer>
  );
}
